import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { useState, type ReactElement } from 'react'

import type { RelatorioSympla } from '../types'

import { cn } from '@/lib/cn'

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

const DIAS_SEMANA = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom']

const MAX_CHIPS_POR_DIA = 2

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

export interface CalendarioEventosProps {
  eventos: NonNullable<RelatorioSympla['agenda']>
  /** Escala de telão: células e tipografia maiores pro canvas de 2500px. */
  telao?: boolean
}

/**
 * Calendário mensal dos próximos eventos: grade seg–dom com os títulos no dia.
 * Só exibição (não é input de data) — os dias chegam como 'YYYY-MM-DD' e toda
 * comparação é por string, sem passar por Date/fuso (regra data-only).
 */
export function CalendarioEventos({ eventos, telao = false }: CalendarioEventosProps): ReactElement {
  const agora = new Date()
  const [ref, setRef] = useState({ ano: agora.getFullYear(), mes: agora.getMonth() + 1 })
  const hojeChave = `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}`

  const chaveMes = `${ref.ano}-${pad(ref.mes)}`
  const porDia = new Map<string, { titulo: string; painelUrl: string | null }[]>()
  for (const e of eventos) {
    if (!e.dia.startsWith(chaveMes)) continue
    porDia.set(e.dia, [...(porDia.get(e.dia) ?? []), { titulo: e.evento, painelUrl: e.painelUrl }])
  }

  // Grade começando na segunda: getDay() dá 0=domingo, deslocamos pra 0=segunda.
  const offset = (new Date(ref.ano, ref.mes - 1, 1).getDay() + 6) % 7
  const totalDias = new Date(ref.ano, ref.mes, 0).getDate()

  function mudarMes(delta: 1 | -1): void {
    setRef(({ ano, mes }) => {
      const proximo = mes + delta
      if (proximo === 0) return { ano: ano - 1, mes: 12 }
      if (proximo === 13) return { ano: ano + 1, mes: 1 }
      return { ano, mes: proximo }
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => mudarMes(-1)}
          aria-label="Mês anterior"
          className={cn(
            'hover:bg-muted flex items-center justify-center rounded-lg border',
            telao ? 'h-14 w-14' : 'h-11 w-11',
          )}
        >
          <ChevronLeft className={cn(telao ? 'h-6 w-6' : 'h-4 w-4')} aria-hidden />
        </button>
        <p className={cn('font-medium capitalize', telao && 'text-3xl')}>
          {MESES[ref.mes - 1]} de {ref.ano}
        </p>
        <button
          type="button"
          onClick={() => mudarMes(1)}
          aria-label="Próximo mês"
          className={cn(
            'hover:bg-muted flex items-center justify-center rounded-lg border',
            telao ? 'h-14 w-14' : 'h-11 w-11',
          )}
        >
          <ChevronRight className={cn(telao ? 'h-6 w-6' : 'h-4 w-4')} aria-hidden />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {DIAS_SEMANA.map((dia) => (
          <p
            key={dia}
            className={cn(
              'text-muted-foreground pb-1 text-center font-semibold uppercase',
              telao ? 'text-lg' : 'text-xs',
            )}
          >
            {dia}
          </p>
        ))}
        {Array.from({ length: offset }, (_, i) => (
          <div key={`vazio-${i}`} aria-hidden />
        ))}
        {Array.from({ length: totalDias }, (_, i) => {
          const dia = i + 1
          const chave = `${chaveMes}-${pad(dia)}`
          const doDia = porDia.get(chave) ?? []
          const extras = doDia.length - MAX_CHIPS_POR_DIA
          return (
            <div
              key={chave}
              data-testid={`dia-${chave}`}
              className={cn(
                'rounded-lg border p-1',
                telao ? 'min-h-28' : 'min-h-16',
                doDia.length > 0 ? 'border-primary/40 bg-primary/5' : 'border-border/60',
                chave === hojeChave && 'ring-primary ring-1',
              )}
            >
              <p
                className={cn(
                  'tabular-nums',
                  telao ? 'text-lg' : 'text-xs',
                  chave === hojeChave ? 'text-primary font-bold' : 'text-muted-foreground',
                )}
              >
                {dia}
              </p>
              {doDia.slice(0, MAX_CHIPS_POR_DIA).map(({ titulo, painelUrl }) => {
                const tamanho = telao ? 'text-sm leading-6' : 'text-[10px] leading-4'
                // Evento com painel público no Hub: o chip abre o painel e ganha
                // destaque (fundo sólido + ícone) pra sinalizar que é clicável.
                return painelUrl ? (
                  <a
                    key={titulo}
                    href={painelUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={`${titulo} — abrir painel público`}
                    className={cn(
                      'bg-primary text-primary-foreground hover:bg-primary/85 mt-0.5 flex items-center gap-0.5 rounded px-1 font-medium',
                      tamanho,
                    )}
                  >
                    <span className="truncate">{titulo}</span>
                    <ExternalLink
                      className={cn('shrink-0', telao ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5')}
                      aria-hidden
                    />
                  </a>
                ) : (
                  <p
                    key={titulo}
                    title={titulo}
                    className={cn('bg-primary/15 text-primary mt-0.5 block truncate rounded px-1', tamanho)}
                  >
                    {titulo}
                  </p>
                )
              })}
              {extras > 0 ? (
                <p
                  className={cn('text-muted-foreground mt-0.5', telao ? 'text-sm' : 'text-[10px]')}
                >
                  +{extras} evento(s)
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
